import {
  getAnswerReferenceByAccessor,
  type AnswerReferenceAccessor,
} from "./answerReferenceRegistry";
import type {
  ConditionalDef,
  ConditionalDefSingle,
  InterviewBlock,
  InterviewItemTemplate,
} from "@/components/interview/types";
import { assertNever } from "@/packages/ts-utils";
import { notNull } from "@/utils/helpers";

export type AnswerReferenceValidation =
  | { valid: true }
  | { valid: false; error: string };

const sortStrings = (values: readonly unknown[]): string[] =>
  values.map(String).sort((a, b) => a.localeCompare(b));

const getCriterionKey = (criterion: ConditionalDefSingle): string =>
  JSON.stringify(
    Object.entries(criterion)
      .filter(([, value]: [string, unknown]) => value !== undefined)
      .map(([key, value]: [string, unknown]): [string, unknown] => [
        key,
        Array.isArray(value) ? sortStrings(value) : value,
      ])
      .sort(([a], [b]) => a.localeCompare(b))
  );

// Ignores the order of criteria and of ids inside a criterion, which carry no meaning.
const getConditionalKey = (conditional: ConditionalDef): string => {
  switch (conditional.type) {
    case "conjunction":
    case "disjunction": {
      const itemKeys = sortStrings(conditional.items.map(getConditionalKey));
      return itemKeys.length === 1
        ? itemKeys[0]
        : JSON.stringify({ type: conditional.type, items: itemKeys });
    }
    case "searchParam":
    case "answerString":
    case "selectedOptions":
    case "selectedTemplateOptions":
    case "concept":
    case "target_group":
    case "audience":
    case "segment":
      return getCriterionKey(conditional);
    default:
      return assertNever(conditional);
  }
};

const areConditionalsEquivalent = (
  a: ConditionalDef,
  b: ConditionalDef | undefined
): boolean => notNull(b) && getConditionalKey(a) === getConditionalKey(b);

export const validateAnswerReferenceEligibility = ({
  sourceItem,
  sourceBlock,
  targetBlock,
  targetConditional,
  accessor,
}: {
  sourceItem: InterviewItemTemplate;
  sourceBlock: InterviewBlock | undefined;
  targetBlock: InterviewBlock | undefined;
  targetConditional: ConditionalDef | undefined;
  accessor: AnswerReferenceAccessor | undefined;
}): AnswerReferenceValidation => {
  if (sourceItem.type !== "question") {
    return {
      valid: false,
      error: "Must reference a question, not a statement",
    };
  }

  const answerReference = getAnswerReferenceByAccessor(accessor);
  if (
    !answerReference ||
    answerReference.questionType !== sourceItem.questionType
  ) {
    return {
      valid: false,
      error:
        accessor === undefined
          ? `${sourceItem.questionType} references require an accessor`
          : `Accessor "${accessor}" is not valid for ${sourceItem.questionType} questions`,
    };
  }

  if (sourceItem.required === false) {
    return {
      valid: false,
      error: "Cannot reference a non-required question that may be skipped",
    };
  }

  if (sourceItem.isSkippable) {
    return {
      valid: false,
      error: "Cannot reference a skippable question",
    };
  }

  // A matching condition guarantees the source was shown, so its answer exists.
  const isSourceConditionUnmatched =
    notNull(sourceItem.conditional) &&
    !areConditionalsEquivalent(sourceItem.conditional, targetConditional);
  if (isSourceConditionUnmatched) {
    return {
      valid: false,
      error:
        "Cannot reference a question with conditional logic unless this question has the same condition",
    };
  }

  const isSourceSectionGated =
    sourceBlock !== undefined &&
    sourceBlock.type !== "screening" &&
    sourceBlock.conditional !== undefined;
  const isCrossSection = sourceBlock?.id !== targetBlock?.id;
  if (isSourceSectionGated && isCrossSection) {
    return {
      valid: false,
      error: "Cannot reference a question in a section with conditional logic",
    };
  }

  if (sourceBlock && targetBlock) {
    const sourceInConcept = sourceBlock.type === "concept";
    const targetInConcept = targetBlock.type === "concept";
    const inDifferentConceptBlocks =
      sourceInConcept && targetInConcept && sourceBlock.id !== targetBlock.id;
    const sourceConceptToGlobal = sourceInConcept && !targetInConcept;

    if (inDifferentConceptBlocks) {
      return {
        valid: false,
        error: "Cannot reference answer from a different concept block",
      };
    }

    if (sourceConceptToGlobal) {
      return {
        valid: false,
        error:
          "Cannot reference concept question answer from a global question",
      };
    }
  }

  return { valid: true };
};
